// Maps the /api/artifact/verify-file response onto the shape the existing
// document-verification wizard pages already read from router state
// (location.state.verificationResult). The wizard pages key off `kind`.

export const DOCUMENT_STEPS = [
  "Upload Document",
  "Automatic Verification",
  "Tamper Detection",
  "Policy & Key Validation",
  "Verification Result",
];

export const ARTIFACT_STEPS = [
  "Upload Artifact",
  "Automatic Verification",
  "Artifact Integrity Analysis",
  "Policy & Key Validation",
  "Verification Result",
];

// A tampered artifact must never look healthy, even if the server reports a
// low risk score (for example when no structural baseline was recorded).
const TAMPERED_MIN_RISK = 90;
const SUSPICIOUS_MIN_RISK = 40;

const signModeOf = (algorithm = "") => {
  if (/\+|hybrid/i.test(algorithm)) return "Hybrid (PQ + ECDSA)";
  if (/dilithium|ml-dsa|falcon|sphincs/i.test(algorithm)) return "Post-Quantum";
  return "Classical (ECDSA)";
};

export function toWizardResult(api) {
  const findings = api.findings || [];
  const serverRisk = api.risk_score ?? 0;
  const seriousFinding = findings.some((f) => f.severity === "critical" || f.severity === "high");

  // Same lower-case labels the document pages compare against ("clean").
  let classification = "clean";
  if (!api.hash_match || !api.signature_valid) classification = "tampered";
  else if (api.verification_status !== "VERIFIED" || seriousFinding) classification = "suspicious";

  // Classification is decided first, then the risk score is raised if the
  // server's number does not agree with it.
  let risk = serverRisk;
  if (classification === "tampered") risk = Math.max(serverRisk, TAMPERED_MIN_RISK);
  else if (classification === "suspicious") risk = Math.max(serverRisk, SUSPICIOUS_MIN_RISK);
  risk = Math.min(100, risk);

  return {
    kind: "artifact",

    // ---- fields the document pages already read ----
    document_valid: api.verification_status === "VERIFIED",
    merkle_valid: !!api.hash_match,                  // "integrity" row
    pq_signature_valid: !!api.signature_valid,       // "signature" row
    blockchain_valid: api.blockchain_valid ?? null,  // null stays "Not Enabled"
    tsa_valid: api.timestamp_valid ?? api.tsa_valid ?? null,
    certificate_valid: api.certificate_valid ?? null,
    verification_score: Math.max(0, 100 - risk),
    sign_mode: signModeOf(api.algorithm),
    algorithm: api.algorithm,
    cid: api.current_hash,                           // shown as "<Artifact> Hash"
    original_ipfs_cid: null,
    stored_merkle_root: api.recorded_hash,
    timestamp: api.timestamp,
    certificate_subject: api.certificate_subject,
    encrypted: false,

    // ---- artifact-only extras ----
    artifact_id: api.artifact_id,
    trust_level: api.trust_level,
    format: api.format,
    file_kind: api.file_kind,
    findings,
    risk_score: risk,
    classification,
    raw: api,
  };
}

// Score props the Result / Report pages expect alongside verificationResult.
export const scoresFrom = (vr) => ({
  aiScore: Math.max(0, 100 - (vr.risk_score ?? 0)),
  riskScore: vr.risk_score ?? 0,
  classification: vr.classification,
});